import type { AppLocale } from './types'

/**
 * The catalogue store.
 *
 * Non-English catalogues arrive **asynchronously** (D-L6: one chunk per locale,
 * fetched once and runtime-cached), so something has to hold them and tell
 * React they landed. That is all this is: a map, a version counter, and a
 * listener set shaped for `useSyncExternalStore`.
 *
 * Module-level rather than context-held on purpose. `copy.<locale>.ts` modules
 * are loaded by a bundler glob with no React in scope, and `getCopy()` is
 * called from mutation callbacks and `lib/` helpers that are not inside a
 * provider at all.
 */
type TranslationBag = Readonly<Record<string, unknown>>

const translations = new Map<string, TranslationBag>()
const listeners = new Set<() => void>()
let version = 0

function key(namespace: string, locale: string): string {
  return `${namespace}:${locale}`
}

function bump(): void {
  version += 1
  for (const listener of listeners) listener()
}

/**
 * Register one feature's catalogue for one locale.
 *
 * `en` is rejected: English is the source of truth declared by `defineCopy`,
 * and a second English catalogue arriving at runtime could only ever disagree
 * with the one TypeScript checked every key against.
 */
export function registerTranslation(namespace: string, locale: AppLocale, translation: TranslationBag): void {
  if (locale === 'en') {
    throw new Error(
      `registerTranslation("${namespace}", "en") — English is the source of truth in copy.ts and cannot be overridden at runtime.`,
    )
  }
  translations.set(key(namespace, locale), translation)
  bump()
}

export function getTranslation(namespace: string, locale: string): TranslationBag | undefined {
  return translations.get(key(namespace, locale))
}

/** `useSyncExternalStore` subscribe. */
export function subscribeTranslations(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** `useSyncExternalStore` snapshot — a plain integer, so identity is stable
 * between registrations and React does not re-render the whole tree on every
 * unrelated read. */
export function translationsVersion(): number {
  return version
}

/**
 * Register several catalogues in ONE notification.
 *
 * Each registration re-renders every `useCopy` consumer (the version moves, so
 * every resolved catalogue is rebuilt). The background fill-in of a whole
 * language registers ~50 catalogues; one at a time that is ~50 full re-renders
 * on a floor tablet at an arbitrary moment. Batched, it is one.
 */
export function registerTranslations(
  entries: readonly { namespace: string; locale: AppLocale; translation: TranslationBag }[],
): void {
  if (entries.length === 0) return
  for (const { namespace, locale, translation } of entries) {
    if (locale === 'en') throw new Error(`registerTranslations("${namespace}", "en") — English cannot be overridden at runtime.`)
    translations.set(key(namespace, locale), translation)
  }
  bump()
}

// ── Per-catalogue loading (the French split, owner decision 2026-09-25) ─────
//
// Each `copy.<locale>.ts` is its own chunk. A screen waits only for the words
// it renders (`catalogueGate`, used by `useCopy`); the rest of the language is
// fetched in the background by the app so it is still there offline.

type CatalogueLoader = () => Promise<unknown>

const loaders = new Map<string, CatalogueLoader>()
const inflight = new Map<string, Promise<TranslationBag | undefined>>()
/** Keys a render must no longer wait for: the load failed or outlived the wait. */
const givenUp = new Set<string>()
/** The one pending wait per catalogue (see `catalogueGate`). */
const gates = new Map<string, Promise<void>>()

/**
 * Declare how to fetch one catalogue. Declaring fetches nothing — it must
 * happen BEFORE the first render, so the first render already knows it can
 * wait for the words instead of flashing English.
 */
export function registerCatalogueLoader(namespace: string, locale: AppLocale, loader: CatalogueLoader): void {
  if (locale === 'en') return
  loaders.set(key(namespace, locale), loader)
}

export function hasCatalogueLoader(namespace: string, locale: string): boolean {
  return loaders.has(key(namespace, locale))
}

/**
 * Fetch one catalogue (deduplicated) WITHOUT registering it. Rejects when the
 * chunk cannot be fetched; a later call tries again (the connection may be
 * back).
 */
export function fetchCatalogue(namespace: string, locale: string): Promise<TranslationBag | undefined> {
  const k = key(namespace, locale)
  const running = inflight.get(k)
  if (running) return running
  const loader = loaders.get(k)
  if (!loader) return Promise.resolve(undefined)
  const promise = loader()
    .then((module) => {
      const translation = (module as { readonly default?: unknown }).default
      return translation && typeof translation === 'object' ? (translation as TranslationBag) : undefined
    })
    .finally(() => {
      inflight.delete(k)
    })
  inflight.set(k, promise)
  return promise
}

/** Fetch and register one catalogue now (a render is waiting for it). */
export async function loadCatalogue(namespace: string, locale: AppLocale): Promise<void> {
  const translation = await fetchCatalogue(namespace, locale)
  if (translation && !translations.has(key(namespace, locale))) registerTranslation(namespace, locale, translation)
}

/** How long a render waits for its words before showing English instead. */
export const CATALOGUE_WAIT_MS = 3000

/**
 * Waiting is for the FIRST screen only. Suspending once something is on screen
 * hides the nearest boundary's content — the page — for the length of a
 * fetch, and whatever had focus goes with it: opening the photo viewer in
 * French within a second of load dropped focus from the thumbnail that opened
 * it (e2e `photo-lightbox`, 2026-09-26). So once the first screen is up
 * (`stopWaitingForCatalogues`, called when the background fill-in starts), a
 * component whose words have not arrived renders English at once and
 * re-renders when they land — a word flickers, the page never disappears.
 */
let waitingAllowed = true

export function stopWaitingForCatalogues(): void {
  waitingAllowed = false
}

/**
 * What a render should do about `namespace` in `locale`:
 * `undefined` — render now (English, no such catalogue, already here, or
 * already given up on); a promise — suspend on it.
 *
 * The promise settles when the words arrive, when the fetch fails (offline,
 * never cached) or after `waitMs` — whichever is first. The last two mark the
 * catalogue given up so no render waits for it again: the screen shows
 * English, and if the chunk lands later it re-renders in the right language.
 * A slow plant network therefore costs at most `waitMs`, never a blank page.
 */
export function catalogueGate(namespace: string, locale: string, waitMs = CATALOGUE_WAIT_MS): Promise<void> | undefined {
  if (locale === 'en') return undefined
  const k = key(namespace, locale)
  if (translations.has(k) || givenUp.has(k) || !loaders.has(k)) return undefined
  if (!waitingAllowed) {
    // Past the first screen: fetch it, but do not hold the render for it.
    if (!inflight.has(k)) void loadCatalogue(namespace, locale as AppLocale).catch(() => givenUp.add(k))
    return undefined
  }
  // One wait per catalogue: React may render a suspended subtree again before
  // the promise settles, and every render must get the SAME promise back.
  const waiting = gates.get(k)
  if (waiting) return waiting
  const load = loadCatalogue(namespace, locale as AppLocale).catch(() => {
    givenUp.add(k)
  })
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      givenUp.add(k)
      resolve()
    }, waitMs)
  })
  const gate = Promise.race([load, late]).finally(() => {
    clearTimeout(timer)
    gates.delete(k)
  })
  gates.set(k, gate)
  return gate
}


/**
 * The locale the app is currently rendering in, mirrored out of React.
 *
 * `getCopy()` needs a locale and its callers — a `lib/` helper, a toast fired
 * from a mutation `onError`, `reportMutationError` in the query client's
 * `MutationCache` — have no hooks available. `I18nProvider` keeps this in step.
 */
let activeLocale: AppLocale = 'en'

export function setActiveLocale(locale: AppLocale): void {
  if (activeLocale === locale) return
  activeLocale = locale
  bump()
}

export function getActiveLocale(): AppLocale {
  return activeLocale
}

/** Test-only: drop every registered catalogue. Not exported from the package
 * index — a production caller emptying the store would silently flip the whole
 * app to English. */
export function resetTranslationsForTest(): void {
  translations.clear()
  loaders.clear()
  inflight.clear()
  givenUp.clear()
  gates.clear()
  waitingAllowed = true
  activeLocale = 'en'
  bump()
}
